import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import { Resource } from "crqlar-react-common/dist/models/resource/resource.model";
import { Sector, SectorType } from "crqlar-react-common/dist/models/sector/sector.model";
import { Service } from "crqlar-react-common/dist/models/service/service.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import { Profile } from "crqlar-react-common/dist/models/profile/profile.model";
import { AgeCategory } from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import { ResourceCategory } from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {QueryObserverResult, useQueryClient} from "react-query";
import { useSector } from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {
    useService,
    useServices,
    useServicesForSector,
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import { useAllAgeCategories } from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {
    useAllResourceCategoriesForSector,
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import { useAllSectors } from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useCallback, useEffect, useMemo} from "react";
import { useShouldDisplayChildrenResources } from "src/utils/reservation.util";
import {useAllChildResources, useAllRootResourcesForSector } from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {
    AVAILABILITY_QUERY_KEY,
    getTimelineSpanDates,
    RESERVATIONS_QUERY_KEY,
    SECTORS_SHOW_CALENDAR_AVAILABILITY,
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {
    useSectorReservations,
    useServiceReservations
} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {
    useAvailabilityCalculationResults
} from "crqlar-react-common/dist/utils/hooks/availability/useResourceAvailability";
import { useProfiles } from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";

interface CalendarDataParams {
    sectorId: string | undefined;
    fromDate: Date;
    toDate: Date;
    selectedStatuses: ReservationStatus[];
    selectedServices: Service[];
    mbscResourceColumnType?: 'resources' | 'services';
    resourceCategoryFilter?: string;
    timelineSpan: 'day' | 'week' | 'month' | undefined;
    selectedAgenda?: AgendaV3;
    calendarNavigationKey?: string
}

interface CalendarData {
    // Core entities
    sector: Sector | undefined;
    defaultService: Service | undefined;

    // Resources
    resourcesFilteredByCategory: Resource[];
    childrenResourcesFilteredByCategory: Resource[];
    allResources: Resource[];
    allChildResources: Resource[];

    // Reservations
    reservations: Reservation[];
    serviceReservations: Reservation[];

    // Related data
    profiles: Profile[];
    availabilitiesCalculationResults: AvailabilityCalculationResult[];
    ageCategories: AgeCategory[];
    resourceCategories: ResourceCategory[];
    sectorServices: Service[];
    services: Service[];

    restaurantSectorId: string | undefined;

    refetch: () => Promise<void>;
    refreshReservations: () => Promise<QueryObserverResult<Reservation[], unknown>>;
    updateReservationInCache: (updatedReservation: Reservation) => void
}

export function useCalendarData(params: CalendarDataParams): CalendarData {
    const {
        sectorId,
        fromDate,
        toDate,
        selectedStatuses,
        selectedServices,
        mbscResourceColumnType,
        resourceCategoryFilter,
        timelineSpan,
        selectedAgenda,
        calendarNavigationKey
    } = params;

    const queryClient = useQueryClient();

    // Base data
    const sector = useSector(sectorId);
    const defaultService = useService(sector?.defaultServiceId);
    const sectorServices = useServicesForSector(sectorId);
    const ageCategories = useAllAgeCategories();
    const resourceCategories = useAllResourceCategoriesForSector(sectorId);
    const allSectors = useAllSectors();

    const restaurantSectorId = useMemo(
        () => allSectors.find(s => s.sectorType === SectorType.RESTAURANT)?.id,
        [allSectors]
    )

    // Resources - compute once
    const usesChildrenResources = useShouldDisplayChildrenResources(sector);

    const resourcesFilteredByCategory = useAllRootResourcesForSector(sectorId, resourceCategoryFilter);
    const allResources = useAllRootResourcesForSector(sectorId);

    const childResourceIds = useMemo(
        () => usesChildrenResources ? resourcesFilteredByCategory.map(r => r.id!) : [],
        [usesChildrenResources, resourcesFilteredByCategory]
    );

    const allChildResourceIds = useMemo(
        () => usesChildrenResources ? allResources.map(r => r.id!) : [],
        [usesChildrenResources, allResources]
    );

    const childrenResourcesFilteredByCategory = useAllChildResources(childResourceIds);
    const allChildResources = useAllChildResources(allChildResourceIds);

    // Reservations
    const startDateFinal = useMemo(() => {
        const [startDate] = getTimelineSpanDates(timelineSpan, fromDate);
        return startDate;
    }, [fromDate, timelineSpan]);


    const reservationsQuery = useSectorReservations(
        [RESERVATIONS_QUERY_KEY, sectorId, startDateFinal, toDate, selectedStatuses, calendarNavigationKey],
        sectorId,
        startDateFinal,
        toDate,
        selectedStatuses,
        undefined,
        {keepPreviousData: true}
    );

    const serviceIds = useMemo(
        () => selectedServices.map(s => s.id).filter(Boolean) as string[],
        [selectedServices]
    );

    const serviceReservationsQuery = useServiceReservations(
        mbscResourceColumnType === 'services' ? serviceIds : [],
        timelineSpan,
        fromDate,
        toDate,
        selectedAgenda?.id,
        {keepPreviousData: true}
    );

    // Profiles - derive IDs from reservations
    const profileIds = useMemo(() => {
        const serviceProfileIds = serviceReservationsQuery.data?.flatMap(sr => sr.profileIds).filter(Boolean) ?? [];
        const reservationProfileIds = reservationsQuery.data?.flatMap(r => r.profileIds ?? []) ?? [];
        return [...serviceProfileIds, ...reservationProfileIds]
    }, [serviceReservationsQuery.data, reservationsQuery.data]);

    // Load the reservations' own profiles without following merges: a followed merge comes back under the main
    // profile's id, which isn't in reservation.profileIds, so slots couldn't find their guest and showed "-".
    // Keep the previous names while a new range loads, otherwise every slot flashes "-"
    const ownProfiles = useProfiles(profileIds as string[], {followMerge: false}, {keepPreviousData: true});
    const mainProfileIds = useMemo(
        () => Array.from(new Set(ownProfiles.flatMap(p => p.mainProfileId ? [p.mainProfileId] : []))),
        [ownProfiles]
    );
    const mainProfiles = useProfiles(mainProfileIds, undefined, {keepPreviousData: true});

    // Merged-away profiles show their main profile's data (as the tooltip does) under the id the reservation references
    const profiles = useMemo(() => ownProfiles.map(profile => {
        if (!profile.mainProfileId) return profile
        const main = mainProfiles.find(m => m.id === profile.mainProfileId || m.originalProfileId === profile.mainProfileId)
        return main ? {...main, id: profile.id, mainProfileId: main.id} : profile
    }), [ownProfiles, mainProfiles]);

    // Availability
    const availabilitiesQuery = useAvailabilityCalculationResults({
        queryKey: [
            AVAILABILITY_QUERY_KEY,
            sectorId,
            resourcesFilteredByCategory.map(res => res.id),
            fromDate,
            toDate,
            selectedStatuses,
            selectedServices.map(s => s.id),
        ],
        entityIds: resourcesFilteredByCategory.map(res => res.id) as string[],
        dateRangeFrom: startOfDay(fromDate),
        dateRangeTo: endOfDay(toDate),
        sectorsShowCalendarAvailability: SECTORS_SHOW_CALENDAR_AVAILABILITY,
        sector,
        keepPreviousData: true,
    });

    const selectedServiceDetails = useServices(serviceIds);
    const services = mbscResourceColumnType === 'services'
        ? serviceIds.length ? selectedServiceDetails : []
        : sectorServices;

    const updateReservationInCache = useCallback((updatedReservation: Reservation) => {
        queryClient.setQueriesData<Reservation[]>(
            { queryKey: [RESERVATIONS_QUERY_KEY], exact: false },
            (old) => {
                if (!old || !Array.isArray(old)) return old ?? [];
                return old.map(r =>
                    r.id === updatedReservation.id ? updatedReservation : r
                );
            }
        );
    }, [queryClient])

    return {
        sector: sector,
        defaultService: defaultService,
        resourcesFilteredByCategory: resourcesFilteredByCategory,
        childrenResourcesFilteredByCategory: childrenResourcesFilteredByCategory,
        allResources: allResources,
        allChildResources: allChildResources,
        reservations: reservationsQuery.data ?? [],
        serviceReservations: serviceReservationsQuery.data ?? [],
        profiles: profiles,
        availabilitiesCalculationResults: availabilitiesQuery.data ?? [],
        ageCategories: ageCategories,
        resourceCategories: resourceCategories,
        sectorServices: sectorServices,
        services: services,
        restaurantSectorId: restaurantSectorId,
        refreshReservations: reservationsQuery.refetch,
        refetch: async () => {
            await Promise.all([
                queryClient.invalidateQueries([RESERVATIONS_QUERY_KEY]),
                queryClient.invalidateQueries([AVAILABILITY_QUERY_KEY]),
            ]);
        },
        updateReservationInCache: updateReservationInCache
    };
}
